import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IEmailDTO } from 'data/dtos/operation/i-email-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import EmailSchema, { IEmailFormData } from 'schema/operation/email'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const EmailForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IEmailFormData>({
    resolver: ajvResolver(EmailSchema),
    defaultValues: {
      identifier: '',
      title: '',
      useLayout: false,
      variables: '',
      status: '',
      howLongInDays: 0,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/emails/${id}`)
        .then((response) => {
          const { data } = response.data

          const emailResult = {
            identifier: data.identifier,
            title: data.title,
            useLayout: data.useLayout,
            variables: data.variables,
            template: data.template,
            status: data.status,
            howLongInDays: data.howLongInDays,
          }

          return emailResult
        })
        .then((emailResult: IEmailDTO) => {
          reset(emailResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IEmailDTO) => {
    setIsLoading(true)

    const payLoad: IEmailDTO = {
      identifier: data.identifier,
      title: data.title,
      useLayout: data.useLayout,
      variables: data.variables,
      template: data.template,
      status: data.status,
      howLongInDays: data.howLongInDays,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/emails`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/emails')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/emails', payLoad)
        .then(() => {
          reset()
          openAlert('Cliente cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  useEffect(() => {
    // setFocus(firstInputElement)
  }, [setFocus])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='E-Mails'
        subTitle='Dados sobre os e-mails da plataforma.'
      />

      <Form.Body>
        <Form.Input
          label='Identificador'
          error={!!errors.identifier}
          errorMessage={errors?.identifier?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='identifier'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Título'
          error={!!errors.title}
          errorMessage={errors?.title?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
          maxLength={100}
          name='title'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.CheckBox
          label='Usa Layout'
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='useLayout'
          control={control}
        />

        <Form.Input
          label='Variáveis'
          error={!!errors.variables}
          errorMessage={errors?.variables?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={4096}
          name='variables'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Template'
          error={!!errors.template}
          errorMessage={errors?.template?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={1024}
          name='template'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Status'
          error={!!errors.status}
          errorMessage={errors?.status?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={100}
          name='status'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Tempo em Dias'
          error={!!errors.howLongInDays}
          errorMessage={errors?.howLongInDays?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          maxLength={3}
          name='howLongInDays'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default EmailForm
